import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Download, Eye, FileText, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

import { classEntryApi } from '../../api/examApi';
import HomeworkFileViewerModal from '../../components/HomeworkFileViewerModal';
import { DashboardLayout } from '../../components/layout';
import UploadHint from '../../components/UploadHint';
import { useAuth } from '../../context/AuthContext';
import { parseApiError } from '../../lib/apiErrors';
import { checkFileSize } from '../../lib/uploadLimits';

const fmtWhen = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const FILE_SECTIONS = [
  { kind: 'notes', label: 'Notes', button: 'Upload notes' },
  { kind: 'homework', label: 'Homework Sheet', button: 'Upload homework sheet' },
];

// Per-student feedback box. Owns its draft + save state and talks only to the
// feedback endpoint, so saving it never touches the batched Done/Not done form.
function FeedbackBox({ entryId, row, isOwner, onSaved }) {
  const saved = row.feedback?.text || '';
  const [draft, setDraft] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => { setDraft(saved); }, [saved]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await classEntryApi.saveHomeworkFeedback(entryId, row.student_id, draft.trim());
      onSaved(row.student_id, data.feedback || null);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to save feedback').message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOwner) {
    return row.feedback ? (
      <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{row.feedback.text}</p>
    ) : null;
  }

  return (
    <div className="space-y-1.5">
      <textarea
        value={draft}
        onChange={(e) => { setDraft(e.target.value); setJustSaved(false); }}
        rows={2}
        maxLength={5000}
        placeholder="Feedback for this student"
        aria-label={`Feedback for ${row.student_name}`}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
        data-testid={`feedback-input-${row.student_id}`}
      />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <button
          onClick={save}
          disabled={saving || draft.trim() === saved}
          className="rounded-md border border-primary px-3 py-1 text-xs font-medium text-primary hover:bg-primary/10 disabled:opacity-40 dark:border-accent-cool dark:text-accent-cool"
          data-testid={`feedback-save-${row.student_id}`}
        >
          {saving ? 'Saving…' : 'Save feedback'}
        </button>
        {justSaved && (
          <span className="text-xs font-medium text-emerald-600" data-testid={`feedback-saved-${row.student_id}`}>Saved</span>
        )}
        {row.feedback && (
          <span className="text-xs text-slate-400" data-testid={`feedback-meta-${row.student_id}`}>
            {row.feedback.author_name ? `${row.feedback.author_name} · ` : ''}{fmtWhen(row.feedback.feedback_at)}
          </span>
        )}
      </div>
    </div>
  );
}

export default function ClassEntryDetailPage() {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  const [title, setTitle] = useState('');
  const [notesMessage, setNotesMessage] = useState('');
  const [facultyComment, setFacultyComment] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  const [uploadingKind, setUploadingKind] = useState(null);
  const inputRefs = { notes: useRef(null), homework: useRef(null) };
  const [viewing, setViewing] = useState(null); // { submissionId, title }

  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [statusByStudent, setStatusByStudent] = useState({});
  const [savedStatus, setSavedStatus] = useState({});
  const [savingHomework, setSavingHomework] = useState(false);

  const isOwner = isAdmin() || (entry && user?.user_id === entry.faculty_id);

  const dirty = useMemo(
    () => Object.keys(statusByStudent).some((id) => statusByStudent[id] !== savedStatus[id]),
    [statusByStudent, savedStatus]
  );

  // Unsaved Done/Not done toggles: warn on tab close/reload and on in-app link
  // clicks. BrowserRouter has no useBlocker, so internal links are caught in
  // the capture phase, before React Router sees the click.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    const onClick = (e) => {
      const a = e.target.closest?.('a[href]');
      if (!a || a.target === '_blank' || a.origin !== window.location.origin) return;
      if (!window.confirm('You have unsaved Done / Not done changes. Leave this page?')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty]);

  const loadEntry = useCallback(() => {
    setLoading(true);
    classEntryApi.get(entryId)
      .then(({ data }) => {
        setEntry(data);
        setTitle(data.title);
        setNotesMessage(data.notes_message || '');
        setFacultyComment(data.faculty_comment || '');
      })
      .catch((err) => toast.error(parseApiError(err, 'Failed to load class entry').message))
      .finally(() => setLoading(false));
  }, [entryId]);

  const loadRoster = useCallback(() => {
    setRosterLoading(true);
    classEntryApi.getHomework(entryId)
      .then(({ data }) => {
        setRoster(data);
        const map = {};
        data.forEach((r) => { if (r.status !== 'pending') map[r.student_id] = r.status; });
        setStatusByStudent(map);
        setSavedStatus(map);
      })
      .catch((err) => toast.error(parseApiError(err, 'Failed to load homework roster').message))
      .finally(() => setRosterLoading(false));
  }, [entryId]);

  useEffect(() => { loadEntry(); }, [loadEntry]);
  useEffect(() => { loadRoster(); }, [loadRoster]);

  const handleBack = () => {
    if (dirty && !window.confirm('You have unsaved Done / Not done changes. Leave this page?')) return;
    navigate('/faculty/classroom');
  };

  const handleFeedbackSaved = (studentId, feedback) => {
    // Touch only this row's feedback; statusByStudent (pending toggles) is left alone.
    setRoster((prev) => prev.map((r) => (r.student_id === studentId ? { ...r, feedback } : r)));
  };

  const handleSaveMeta = async (e) => {
    e.preventDefault();
    setSavingMeta(true);
    try {
      const { data } = await classEntryApi.update(entryId, {
        title: title.trim(),
        notes_message: notesMessage.trim() || null,
        faculty_comment: facultyComment.trim() || null,
      });
      setEntry(data);
      toast.success('Class entry updated');
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to update class entry').message);
    } finally {
      setSavingMeta(false);
    }
  };

  const handleUpload = (kind) => async (e) => {
    const input = inputRefs[kind].current;
    const file = e.target.files?.[0];
    if (!file) return;
    const sizeError = checkFileSize(file);
    if (sizeError) {
      toast.error(sizeError);
      if (input) input.value = '';
      return;
    }
    const formData = new FormData();
    formData.append('kind', kind);
    formData.append('file', file);
    setUploadingKind(kind);
    try {
      await classEntryApi.uploadFile(entryId, formData);
      toast.success('File uploaded');
      loadEntry();
    } catch (err) {
      toast.error(parseApiError(err, 'Upload failed').message);
    } finally {
      setUploadingKind(null);
      if (input) input.value = '';
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await classEntryApi.downloadFile(entryId, file.id);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${entry.title}_${file.kind}${file.id.slice(0, 8)}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error(parseApiError(err, 'Download failed').message);
    }
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await classEntryApi.deleteFile(entryId, file.id);
      toast.success('File deleted');
      loadEntry();
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to delete file').message);
    }
  };

  const handleDeleteEntry = async () => {
    if (!window.confirm('Delete this class entry? This removes all its files and homework records.')) return;
    try {
      await classEntryApi.remove(entryId);
      toast.success('Class entry deleted');
      navigate('/faculty/classroom');
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to delete class entry').message);
    }
  };

  const setStatus = (studentId, status) => {
    if (!isOwner) return;
    setStatusByStudent((prev) => ({ ...prev, [studentId]: status }));
  };

  const handleSaveHomework = async () => {
    const marks = roster
      .filter((r) => statusByStudent[r.student_id])
      .map((r) => ({ student_id: r.student_id, status: statusByStudent[r.student_id] }));
    if (marks.length === 0) {
      toast.error('Mark at least one student before saving');
      return;
    }
    setSavingHomework(true);
    try {
      await classEntryApi.markHomework(entryId, marks);
      toast.success('Homework status saved');
      loadRoster();
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to save homework status').message);
    } finally {
      setSavingHomework(false);
    }
  };

  if (loading || !entry) {
    return (
      <DashboardLayout title="Classroom">
        <div className="animate-pulse space-y-4 max-w-3xl">
          <div className="h-8 w-64 rounded bg-slate-200 dark:bg-slate-700" />
          <div className="h-32 rounded bg-slate-100 dark:bg-slate-800" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Classroom">
      <div className="animate-fade-in max-w-3xl space-y-6">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary dark:text-slate-400 dark:hover:text-accent-cool"
        >
          <ArrowLeft size={16} /> Back to Classroom
        </button>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">{entry.title}</h2>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {entry.group_name} · {new Date(`${entry.date}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })} · by {entry.faculty_name}
            </p>
          </div>
          {isOwner && (
            <button
              onClick={handleDeleteEntry}
              className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 dark:border-rose-900/40 dark:text-rose-400 dark:hover:bg-rose-900/10"
              data-testid="delete-entry-btn"
            >
              <Trash2 size={16} /> Delete Entry
            </button>
          )}
        </div>

        {!isOwner && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
            View only — only {entry.faculty_name} or an admin can edit this entry.
          </div>
        )}

        <form onSubmit={handleSaveMeta} className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800 space-y-4">
          <h3 className="text-lg font-heading font-semibold text-slate-900 dark:text-white">Details</h3>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={!isOwner}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              data-testid="entry-title-field"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Notes / Message</label>
            <textarea
              value={notesMessage}
              onChange={(e) => setNotesMessage(e.target.value)}
              disabled={!isOwner}
              rows={3}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Faculty Comment</label>
            <textarea
              value={facultyComment}
              onChange={(e) => setFacultyComment(e.target.value)}
              disabled={!isOwner}
              rows={2}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
          </div>
          {isOwner && (
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={savingMeta || !title.trim()}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-light disabled:opacity-50"
                data-testid="save-entry-meta"
              >
                {savingMeta ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          )}
        </form>

        {FILE_SECTIONS.map(({ kind, label, button }) => {
          const files = entry.files.filter((f) => f.kind === kind);
          return (
            <div key={kind} className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800 space-y-3" data-testid={`files-section-${kind}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <h3 className="text-lg font-heading font-semibold text-slate-900 dark:text-white">{label}</h3>
                {isOwner && (
                  <div className="flex flex-col items-start gap-1 sm:items-end">
                    <input
                      ref={inputRefs[kind]}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleUpload(kind)}
                      className="hidden"
                      id={`class-entry-file-input-${kind}`}
                      data-testid={`upload-input-${kind}`}
                    />
                    <label
                      htmlFor={`class-entry-file-input-${kind}`}
                      className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-light ${uploadingKind ? 'pointer-events-none opacity-50' : ''}`}
                      data-testid={`upload-btn-${kind}`}
                    >
                      <Upload size={14} /> {uploadingKind === kind ? 'Uploading…' : button}
                    </label>
                    <UploadHint types="PDF, JPG or PNG" />
                  </div>
                )}
              </div>

              {files.length === 0 ? (
                <p className="text-sm text-slate-400">No files uploaded yet.</p>
              ) : (
                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                  {files.map((file) => (
                    <div key={file.id} className="flex items-center justify-between py-2.5" data-testid={`entry-file-${file.id}`}>
                      <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                        <FileText size={16} className="text-slate-400" />
                        <span className="text-xs text-slate-500">{fmtWhen(file.uploaded_at)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownload(file)}
                          className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
                          title="Download"
                          aria-label="Download"
                        >
                          <Download size={16} />
                        </button>
                        {isOwner && (
                          <button
                            onClick={() => handleDeleteFile(file)}
                            className="rounded-md p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30"
                            title="Delete"
                            aria-label="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          <div className="flex items-center justify-between p-6 pb-4">
            <div>
              <h3 className="text-lg font-heading font-semibold text-slate-900 dark:text-white">Homework Status</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                {roster.filter((r) => statusByStudent[r.student_id]).length} of {roster.length} marked
                {dirty && <span className="ml-2 text-amber-600" data-testid="status-dirty-note">· unsaved status changes</span>}
              </p>
            </div>
          </div>

          {rosterLoading ? (
            <div className="px-6 pb-6 space-y-2 animate-pulse">
              <div className="h-10 rounded bg-slate-100 dark:bg-slate-700" />
              <div className="h-10 rounded bg-slate-100 dark:bg-slate-700" />
            </div>
          ) : roster.length === 0 ? (
            <p className="px-6 pb-6 text-sm text-slate-400">No approved students in this group.</p>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {roster.map((r) => {
                const status = statusByStudent[r.student_id];
                const pending = status !== savedStatus[r.student_id];
                return (
                  <div key={r.student_id} className="space-y-3 px-6 py-4" data-testid={`homework-row-${r.student_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-900 dark:text-white">
                        {r.student_name}
                        {pending && <span className="ml-2 text-xs font-normal text-amber-600" data-testid={`unsaved-${r.student_id}`}>unsaved</span>}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setStatus(r.student_id, 'done')}
                          disabled={!isOwner}
                          data-testid={`homework-done-${r.student_id}`}
                          aria-pressed={status === 'done'}
                          className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                            status === 'done'
                              ? 'bg-emerald-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700 dark:bg-slate-700 dark:text-slate-300'
                          }`}
                        >
                          <Check size={13} /> Done
                        </button>
                        <button
                          onClick={() => setStatus(r.student_id, 'not_done')}
                          disabled={!isOwner}
                          data-testid={`homework-not-done-${r.student_id}`}
                          aria-pressed={status === 'not_done'}
                          className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                            status === 'not_done'
                              ? 'bg-rose-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-rose-100 hover:text-rose-700 dark:bg-slate-700 dark:text-slate-300'
                          }`}
                        >
                          <X size={13} /> Not Done
                        </button>
                      </div>
                    </div>

                    <div data-testid={`student-files-${r.student_id}`}>
                      {r.files?.length ? (
                        <div className="flex flex-wrap gap-2">
                          {r.files.map((f) => (
                            <button
                              key={f.id}
                              onClick={() => setViewing({ submissionId: f.id, title: `${r.student_name} — ${f.original_name}` })}
                              className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                              data-testid={`student-file-${f.id}`}
                            >
                              <Eye size={12} className="shrink-0" />
                              <span className="truncate">{f.original_name}</span>
                              <span className="shrink-0 text-slate-400">{fmtWhen(f.uploaded_at)}</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400">No upload</p>
                      )}
                    </div>

                    <FeedbackBox entryId={entryId} row={r} isOwner={isOwner} onSaved={handleFeedbackSaved} />
                  </div>
                );
              })}
            </div>
          )}

          {isOwner && roster.length > 0 && (
            <div className="flex justify-end p-6 pt-4">
              <button
                onClick={handleSaveHomework}
                disabled={savingHomework || rosterLoading}
                className="rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-white hover:bg-primary-light disabled:opacity-50"
                data-testid="save-homework-btn"
              >
                {savingHomework ? 'Saving...' : 'Save Homework Status'}
              </button>
            </div>
          )}
        </div>
      </div>

      {viewing && (
        <HomeworkFileViewerModal
          title={viewing.title}
          fetchBlob={() => classEntryApi.downloadStudentHomework(entryId, viewing.submissionId).then((r) => r.data)}
          onClose={() => setViewing(null)}
        />
      )}
    </DashboardLayout>
  );
}
